import type { Bundle } from '@medplum/fhirtypes';
import { useSubscription } from '@medplum/react-hooks';
import { useCallback, useEffect, useRef } from 'react';

const isDev = import.meta.env.DEV;

export interface UseMedplumSubscriptionRefreshOptions {
  /** Debounce window for coalescing burst events (default 300ms). */
  debounceMs?: number;
  /** When false, no WebSocket subscription is opened (default true). */
  enabled?: boolean;
  /** Dev-only console prefix, e.g. `[Chart][WS]`. */
  debugLabel?: string;
}

/**
 * Debounced refetch when FHIR resources matching `criteria` change via WebSocket.
 * Refetches immediately on subscription reconnect to catch missed events.
 */
export function useMedplumSubscriptionRefresh(
  criteria: string | undefined,
  refresh: () => void,
  options?: UseMedplumSubscriptionRefreshOptions,
): void {
  const { debounceMs = 300, enabled = true, debugLabel = '[Medplum][WS]' } = options ?? {};
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRefresh = useCallback(() => {
    if (timerRef.current != null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (isDev) {
        console.log(`${debugLabel} Event received → scheduling refetch`);
      }
      refreshRef.current();
    }, debounceMs);
  }, [debounceMs, debugLabel]);

  const handleBundle = useCallback((_bundle: Bundle) => {
    scheduleRefresh();
  }, [scheduleRefresh]);

  const handleSubscriptionConnect = useCallback(() => {
    if (isDev) {
      console.log(`${debugLabel} Subscription connected/reconnect — criteria:`, criteria);
    }
    refreshRef.current();
  }, [criteria, debugLabel]);

  const handleWebSocketOpen = useCallback(() => {
    if (isDev) {
      console.log(`${debugLabel} Connected — criteria:`, criteria);
    }
  }, [criteria, debugLabel]);

  const handleWebSocketClose = useCallback(() => {
    if (isDev) {
      console.log(`${debugLabel} Disconnected — criteria:`, criteria);
    }
  }, [criteria, debugLabel]);

  const handleError = useCallback((err: Error) => {
    console.warn(`${debugLabel} Subscription error:`, err.message, 'criteria:', criteria);
  }, [criteria, debugLabel]);

  useSubscription(enabled ? criteria : undefined, handleBundle, {
    onSubscriptionConnect: handleSubscriptionConnect,
    onWebSocketOpen: handleWebSocketOpen,
    onWebSocketClose: handleWebSocketClose,
    onError: handleError,
  });

  useEffect(() => {
    if (isDev && enabled && criteria) {
      console.log(`${debugLabel} Subscribing — criteria:`, criteria);
    }
  }, [criteria, enabled, debugLabel]);

  useEffect(() => {
    return () => {
      if (timerRef.current != null) clearTimeout(timerRef.current);
    };
  }, []);
}
